import { SearchDisplayFieldStatus } from 'business/models/Models';
import RMTable from 'components/customComponents/Controls/RMTable';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import LoadingDiv from 'components/generic/loadingDiv';
import { useModalHandlers } from 'components/modal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import {
  useIsSearchInProgress,
  useSearchDisplayFields,
  useSearchResult,
} from 'redux/hooks/searchnFilter';
import { setSelectedRowIndices } from 'redux/reducers/searchnFilterReducer';
import { Footer } from './footer';

interface Props {
  columnsToDisplay?: string[];
  showPaginator?: boolean;
  customSelectionChange?: (rowIndices: number[]) => void;
}

type SortInfo = { key?: string; type: 'asc' | 'desc' };

const getSortedItem = (items: { [key: string]: any }[], info: SortInfo) => {
  const { key, type } = info;
  return [...items].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    // Handle undefined or null
    if (valA == null) {
      return 1;
    }
    if (valB == null) {
      return -1;
    }

    if (typeof valA === 'number' && typeof valB === 'number') {
      return type === 'asc' ? valA - valB : valB - valA;
    }

    // Compare as strings
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();

    if (strA < strB) {
      return type === 'asc' ? -1 : 1;
    }
    if (strA > strB) {
      return type === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

const Result = (props: Props) => {
  const { columnsToDisplay, showPaginator = true, customSelectionChange } = props;

  const searchResult = useSearchResult();
  const displayFields = useSearchDisplayFields();
  const dispatch = useRMDispatch();
  const { showModal, hideModal } = useModalHandlers();
  const IssearchInProgress = useIsSearchInProgress();
  const [sortInfo, setSortInfo] = useState<SortInfo>({ key: null, type: 'asc' });

  useEffect(() => {
    if (!IssearchInProgress && searchResult.rows.items.length === 0) {
      showModal({
        body: 'No records match your search. Adjust your specifications and try again.',
        onOk: hideModal,
        heading: 'No Record Found',
        okButtonText: 'Ok',
        isOkOnly: true,
      });
    }
  }, [searchResult, IssearchInProgress]);

  const sortedResult = useMemo(() => {
    if (!searchResult?.rows?.items) {
      return [];
    }
    if (!sortInfo.key) {
      return [...searchResult.rows.items];
    }

    return getSortedItem(searchResult.rows.items, sortInfo);
  }, [searchResult?.rows?.items, sortInfo]);

  const getOriginalIndexFromSortedIndex = useCallback(
    (sortedIndex: number): number => {
      if (!searchResult?.rows?.items?.length && !sortedResult?.length) {
        return -1;
      }

      if (!sortInfo.key) {
        return sortedIndex;
      }

      const sortedItem = sortedResult[sortedIndex];
      if (!sortedItem) {
        return -1;
      }

      return searchResult.rows.items.findIndex((item) => item === sortedItem);
    },
    [searchResult?.rows?.items, sortedResult, sortInfo.key],
  );

  const getSortedIndexFromOriginalIndex = useCallback(
    (originalIndex: number): number => {
      if (!searchResult?.rows?.items?.length && !sortedResult?.length) {
        return -1;
      }

      if (!sortInfo.key) {
        return originalIndex;
      }

      const originalItem = searchResult.rows.items[originalIndex];
      if (!originalItem) {
        return -1;
      }

      return sortedResult.findIndex((item) => item === originalItem);
    },
    [searchResult?.rows?.items, sortedResult, sortInfo.key],
  );

  const selectedSortedRowIndices = useMemo(
    () =>
      (searchResult.selectedRowIndices || [])
        .map((idx) => getSortedIndexFromOriginalIndex(idx))
        .filter((idx) => idx >= 0),
    [searchResult.selectedRowIndices, getSortedIndexFromOriginalIndex],
  );

  const onSortClick = (field: string) => {
    setSortInfo((prev) => ({
      key: field,
      type: prev.key === field && prev.type === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleTableFilter = (filterTitle: string) => (event: any) => {
    return (
      <TableSortInputFilter
        event={event}
        title={filterTitle}
        showSearchInput={false}
        onSortClick={() => onSortClick(filterTitle)}
      />
    );
  };

  const columns = useMemo(() => {
    if (columnsToDisplay) {
      return columnsToDisplay.map((col) => ({
        accessor: col,
        Header: col,
        Filter: handleTableFilter(col),
      }));
    } else {
      return displayFields
        ?.filter((sf) => sf.status === SearchDisplayFieldStatus.Display)
        .map((sf) => ({
          accessor: sf.columnHeader,
          Header: sf.columnHeader,
          Filter: handleTableFilter(sf.columnHeader),
          className:
            sf.columnHeader == 'Experiment.Name' ? ' max-w-[17rem] w-[17rem]' : '',
        }));
    }
  }, [displayFields, columnsToDisplay, setSortInfo, sortInfo]);

  const handleSelectionChange = (rowIndices) => {
    const originalIndices =
      rowIndices
        ?.map((idx) => getOriginalIndexFromSortedIndex(idx))
        .filter((idx) => idx >= 0) || [];

    if (customSelectionChange) {
      customSelectionChange(originalIndices);
    } else {
      if (originalIndices.length > 0) {
        dispatch(setSelectedRowIndices([...originalIndices]));
      } else {
        dispatch(setSelectedRowIndices([0]));
      }
    }
  };

  return (
    <div id="index-result-main-container" className="flex flex-col h-full w-full px-1">
      <div id="index-result-search-result" className="basis-11/12 max-h-[91.66%]">
        {searchResult.isFetching ? (
          <LoadingDiv />
        ) : (
          <RMTable
            data={sortedResult}
            columns={columns}
            selectedIds={selectedSortedRowIndices}
            onCheckboxSelectionChange={handleSelectionChange}
            onRowClicked={(row) => handleSelectionChange([row.index])}
            enableMultiSelection
            rowClassNamePostfix="-noflex"
            tableClass="w-full"
            scrollToSelected={true}
            disableUnsort
          />
        )}
      </div>
      {showPaginator && (
        <div id="index-result-pagination" className="basis-1/12">
          <Footer />
        </div>
      )}
    </div>
  );
};

export default Result;
